import React, { useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { db, functions } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';

const ADMIN_EMAIL = 'ccrane2@alumni.nd.edu';

function statusLabel(g) {
  if (g.status === 'completed') {
    const myScore = g.scores.player1;
    const botScore = g.scores.player2;
    const result = myScore > botScore ? 'Won' : myScore < botScore ? 'Lost' : 'Tied';
    return `${result} ${myScore} to ${botScore}`;
  }
  return `Round ${g.round}`;
}

/**
 * Practice games against the bot -- same server-authoritative architecture
 * as a real online game (see PracticeGame.js), but stored in their own
 * collection so they never touch real game history/stats.
 */
export default function PracticeGames() {
  const { currentUser, authLoaded } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!currentUser || currentUser.email !== ADMIN_EMAIL) return;
    getDocs(collection(db, 'practiceGames')).then(snap => {
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      loaded.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      setGames(loaded);
    });
  }, [currentUser]);

  if (!authLoaded) return <p>Loading…</p>;
  if (currentUser?.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;

  async function handleNewGame() {
    setCreating(true);
    setError(null);
    try {
      const createPracticeGame = httpsCallable(functions, 'createPracticeGame');
      const result = await createPracticeGame();
      navigate(`/dev/practice-game/${result.data.practiceGameId}`);
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  }

  return (
    <div className="panel w-100">
      <div className="mb-3">
        <Link to="/dev">← Dev</Link>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="section-title mb-0">Practice Game</h2>
        <Button onClick={handleNewGame} disabled={creating}>
          {creating ? 'Starting…' : '+ New Practice Game'}
        </Button>
      </div>
      <p className="text-muted">Play a full game against a moderate-skill computer opponent. Nothing here counts toward real stats or history.</p>

      {error && <Alert variant="danger">{error}</Alert>}

      {games === null ? (
        <p>Loading…</p>
      ) : games.length === 0 ? (
        <p>No practice games yet.</p>
      ) : (
        games.map(g => (
          <div
            key={g.id}
            className="recent-game-row"
            onClick={() => navigate(`/dev/practice-game/${g.id}`)}
            style={{ cursor: 'pointer' }}
          >
            <span>vs Computer</span>
            <span className="text-muted recent-game-date">{statusLabel(g)}</span>
          </div>
        ))
      )}
    </div>
  );
}
